import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';

/**
 * Walkthrough and demo media browser.
 *
 * One stage, one playlist, and at most ONE YouTube iframe on the page at a
 * time. A library can carry a dozen videos; embedding those would pull several
 * hundred kilobytes of third-party script and a dozen players before the
 * visitor has asked for any of them, so nothing but poster images is rendered
 * until the play control is pressed.
 *
 * Selecting from the playlist re-points the stage rather than adding to it. If
 * a video is already playing, the old iframe is torn down and the newly chosen
 * one starts - the selection is itself the gesture, so autoplay is allowed. If
 * nothing is playing, selecting only swaps the poster and title and waits for
 * Play, which keeps the no-iframe-until-asked promise intact.
 *
 * It joins Horizon's single-media protocol, so starting a video pauses the
 * global audio player and vice versa.
 */
class AsxVideoBrowser extends Component {
  requiredRefs = ['frame', 'poster', 'title', 'play'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      ThemeEvents.mediaStartedPlaying,
      (event) => {
        if (/** @type {any} */ (event).detail?.resource === this) return;
        this.#teardown();
      },
      { signal: this.#abortController.signal }
    );

    // YouTube generates `maxresdefault` only for videos uploaded above a certain
    // size, so the highest still is requested and the next one down is used when
    // it 404s. Handled here rather than with an inline `onerror` so the markup
    // stays script-free.
    //
    // Bound to the stage image itself rather than to whatever currently carries
    // a fallback chain: the stage keeps the same <img> across selections, so a
    // listener attached once covers every video, including the ones selected
    // after a product whose lead video had its own authored poster.
    this.refs.poster?.addEventListener('error', this.#stepDownPoster, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Attached directly to the image rather than delegated, so `currentTarget` is
   * the image - and `error` does not bubble, so it could not be delegated.
   * @param {Event} event
   */
  #stepDownPoster = (event) => {
    const image = event.currentTarget;
    if (!(image instanceof HTMLImageElement)) return;

    const remaining = (image.dataset.posterFallback || '').split('|').filter(Boolean);
    const next = remaining.shift();
    if (!next) return;

    image.dataset.posterFallback = remaining.join('|');
    image.removeAttribute('srcset');
    image.src = next;
  };

  /** The playlist button for the video currently on the stage. */
  get #current() {
    return this.querySelector('[data-video-id][aria-current="true"]');
  }

  /**
   * Points the stage at a playlist entry. Bound declaratively from the markup.
   *
   * `target`, not `currentTarget`: Horizon's `on:` bindings are delegated from
   * the document and hand back an event proxied so that `target` is the element
   * carrying the attribute. `currentTarget` on that event is the document.
   *
   * @param {Event} event
   */
  select = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const id = button.dataset.videoId;
    if (!id || button.getAttribute('aria-current') === 'true') return;

    const wasPlaying = this.dataset.loaded === 'true';
    this.#teardown();

    for (const item of this.querySelectorAll('[data-video-id]')) {
      item.setAttribute('aria-current', item === button ? 'true' : 'false');
    }

    const poster = this.refs.poster;
    if (poster instanceof HTMLImageElement) {
      poster.removeAttribute('srcset');
      poster.src = button.dataset.stagePoster || poster.src;
      poster.dataset.posterFallback = button.dataset.stageFallback || '';
      poster.alt = button.dataset.videoTitle || '';
    }

    const title = this.refs.title;
    if (title instanceof HTMLElement) title.textContent = button.dataset.videoTitle || '';

    const play = this.refs.play;
    if (play instanceof HTMLElement) {
      play.setAttribute('aria-label', (play.dataset.labelTemplate || '').replace('%s', button.dataset.videoTitle || ''));
    }

    // Swapping while a video plays continues playing; swapping while the stage
    // is a poster leaves it a poster.
    if (wasPlaying) this.activate();
  };

  /** Replaces the stage poster with a playing iframe. Bound declaratively. */
  activate = () => {
    if (this.dataset.loaded === 'true') return;

    const current = this.#current;
    const id = current instanceof HTMLElement ? current.dataset.videoId : this.dataset.videoId;
    const target = this.refs.frame;
    if (!id || !(target instanceof HTMLElement)) return;

    // A single-video product has no playlist to read the title from.
    const label =
      (current instanceof HTMLElement && current.dataset.videoTitle) ||
      this.refs.title?.textContent?.trim() ||
      'Video';

    document.dispatchEvent(new MediaStartedPlayingEvent(this));

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1`;
    iframe.title = label;
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.loading = 'lazy';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.className = 'asx-vb__iframe';

    target.replaceChildren(iframe);
    this.dataset.loaded = 'true';
    iframe.focus();
  };

  /** Returns the stage to its poster, disposing of the player with it. */
  #teardown() {
    if (this.dataset.loaded !== 'true') return;
    const target = this.refs.frame;
    if (target instanceof HTMLElement) target.replaceChildren();
    this.dataset.loaded = 'false';
  }
}

if (!customElements.get('asx-video-browser')) {
  customElements.define('asx-video-browser', AsxVideoBrowser);
}

export default AsxVideoBrowser;
